import React from "react";
import { useDispatch } from "react-redux";
import { string, number, object, func, arrayOf } from "prop-types";
import { twMerge } from "tailwind-merge";

import api from "@api";
import gtm from "@gtm/core";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useActiveCarPreconfigurationSelector } from "@redux/reducers/deal";
import { changeConfigurationEvent } from "@gtm/events/homepage";
import {
  removePromoCodeAction,
  updateCarPreconfigurationAction,
} from "@redux/actions/dealActions";
import { setLastChangedAtAction } from "@redux/actions/deprecatedActions";
import Typography from "@components/shared/Typography";
import SubMenu from "./SubMenu";
import { useDetectDevice } from "@hooks/useDetectDevice";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import { withTabNavigation } from "@hoc/withTabNavigation";
import ArrowRightIcon from "@components/shared/Icons/ArrowRight";

import { sessionStorageClient } from "utils/sessionStorageClient";
import {
  PRECONFIGURATIONS_GROUPS,
  SESSION_STORAGE_KEYS,
} from "@shared/constants";
import Icon from "@components/shared/Icons/Icon";

const ItemWrap = withTabNavigation("div");

function getPickerPathname(subGroup, singlePreconfiguration) {
  const basePath = "/images/preconfigs/";

  if (subGroup && subGroup.length) {
    if (subGroup[0].subgroupLabel === PRECONFIGURATIONS_GROUPS.AMI) {
      return `${basePath}picker_ami.svg`;
    }
    if (subGroup[0].subgroupLabel === PRECONFIGURATIONS_GROUPS.BUGGY) {
      return `${basePath}picker_buggy.svg`;
    }

    return `${basePath}picker_colors.svg`;
  }

  return `${basePath}${singlePreconfiguration.id}/picker.svg`;
}

const PreconfigurationItem = ({
  singlePreconfiguration,
  preconfigurationSubGroup,
  setSelectedSubgroup,
  label,
  index = 0,
}) => {
  const dispatch = useDispatch();
  const { isDesktop } = useDetectDevice();
  const isUserTrusted = useIsUserTrustedSelector();

  const {
    id: activePreconfigurationId,
  } = useActiveCarPreconfigurationSelector();

  const assetUrl = getPickerPathname(
    preconfigurationSubGroup,
    singlePreconfiguration
  );

  const onPreconfigurationChange = preconfiguration => {
    if (preconfiguration.id === activePreconfigurationId) {
      return;
    }

    if (!isUserTrusted) {
      api.removePromoCodeFromSession();
    }

    dispatch(removePromoCodeAction());
    dispatch(updateCarPreconfigurationAction(preconfiguration));
    dispatch(setLastChangedAtAction(Date.now()));

    sessionStorageClient.removeItem(SESSION_STORAGE_KEYS.PROMOCODE);

    gtm.fire(changeConfigurationEvent(preconfiguration.label));
  };

  const isSubGroupActive = preconfigurationSubGroup?.some(
    p => p.id === activePreconfigurationId
  );

  const preconfigurationCount = preconfigurationSubGroup?.length;

  const isItemWithSubmenu = !!preconfigurationSubGroup;

  const isItemActive =
    activePreconfigurationId === singlePreconfiguration?.id || isSubGroupActive;

  const onClickItem = () => {
    if (!isItemWithSubmenu) {
      onPreconfigurationChange(singlePreconfiguration);

      return;
    }

    if (isDesktop) {
      return;
    }

    setSelectedSubgroup(preconfigurationSubGroup);
  };

  return (
    <ItemWrap
      active={isItemActive}
      className="
        flex justify-start items-center w-full py-1 relative cursor-pointer group
        min-w-50
        [&:hover>div>p]:border-b-white
        [&:hover>div>span]:ring-white
      "
      onClick={onClickItem}
    >
      <span
        className="flex size-6 basis-6 shrink-0 grow-0"
        data-id={`preconfiguration-color-${index}`}
      >
        <img
          alt={label}
          className={twMerge(
            "size-full rounded-full",
            "transition duration-150",
            isItemActive && "ring-2 ring-white"
          )}
          src={getStaticAssetURL(assetUrl)}
          title={label}
        />
      </span>
      <div className="px-4 mr-auto">
        <Typography
          className={twMerge(
            "text-white text-2xl/1 font-extrabold mt-1",
            "transition duration-150",
            "border-b-2 border-b-transparent hover:border-b-white",
            isItemActive && "border-b-white"
          )}
        >
          {label}
          {preconfigurationCount && ` (${preconfigurationCount})`}
        </Typography>

        <div
          className={twMerge(
            "w-full h-1 bg-white",
            activePreconfigurationId === singlePreconfiguration?.id ||
              isSubGroupActive
              ? "opacity-100"
              : "opacity-0"
          )}
        />
      </div>
      {isItemWithSubmenu && (
        <>
          <Icon className="size-5 text-white" icon={<ArrowRightIcon />} />
          <SubMenu preconfigurationSubGroup={preconfigurationSubGroup} />
        </>
      )}
    </ItemWrap>
  );
};

PreconfigurationItem.propTypes = {
  label: string.isRequired,
  setSelectedSubgroup: func,
  singlePreconfiguration: object,
  index: number,
  preconfigurationSubGroup: arrayOf(object),
};

export default PreconfigurationItem;
